import { generateKey, exportKey, importKey, encryptText, decryptText, generateProductionDeviceIdentity, type ProductionDeviceIdentity } from "@echopaste/crypto";
import { PROTOCOL_VERSION, detectClipType, type ClipEnvelope } from "@echopaste/protocol";
import { activateProductionRoom, loadProductionDeviceIdentity, loadProductionRoom, savePendingProductionRoom, saveProductionDeviceIdentity, unwrapProductionRoomKey, getVersionedRoomKey, unwrapVersionedRoomKey } from "./storage/db";
import { signedProductionFetch } from "./productionAuth";
import { getCachedPlatform } from "./platform";

export interface PersonalSession { roomId: string; identity: ProductionDeviceIdentity; key: CryptoKey; keyVersion: number; }
export interface PersonalClip extends ClipEnvelope { plaintext: string; unavailable?: boolean; }

export async function responseJson(response: Response): Promise<any> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) throw new Error(data.error || `服务器返回 ${response.status}，请检查连接。`);
  return data;
}

export async function loadPersonalSession(roomId: string): Promise<PersonalSession | null> {
  const identity = await loadProductionDeviceIdentity();
  const room = await loadProductionRoom(roomId);
  if (!identity || !room || room.state !== "active") return null;
  return { roomId, identity, key: await importKey(await unwrapProductionRoomKey(room), { extractable: false }), keyVersion: room.keyVersion };
}

export async function setupPersonalRoom(apiBase: string, roomId: string, setupCode: string): Promise<PersonalSession> {
  let identity = await loadProductionDeviceIdentity();
  if (!identity) { await saveProductionDeviceIdentity(await generateProductionDeviceIdentity()); identity = await loadProductionDeviceIdentity(); }
  if (!identity) throw new Error("无法保存设备身份，请允许应用存储数据。");
  // Persist keys BEFORE the network operation; retries never replace the content key.
  if (!await loadProductionRoom(roomId)) await savePendingProductionRoom({ roomId, keyVersion: 1, rawRoomKeyMaterial: await exportKey(await generateKey()) });
  const platform = getCachedPlatform();
  const device = { deviceId: identity.deviceId, signingPublicKeySpki: identity.signingPublicKeySpki, signingFingerprint: identity.signingFingerprint, agreementPublicKeySpki: identity.agreementPublicKeySpki, agreementFingerprint: identity.agreementFingerprint, platform: platform === "android" || platform === "windows" ? platform : "web", displayName: platform === "android" ? "我的手机" : "我的电脑" };
  await responseJson(await signedProductionFetch({ apiBase, identity, roomId, path: "/api/v1/bootstrap", method: "POST", headers: { "Content-Type": "application/json", "X-EchoPaste-Setup": setupCode.trim() }, body: JSON.stringify({ roomId, device }) }));
  await activateProductionRoom(roomId);
  return (await loadPersonalSession(roomId))!;
}

export async function sendPersonalClip(apiBase: string, session: PersonalSession, text: string): Promise<ClipEnvelope> {
  if (!text.trim()) throw new Error("剪贴板或输入框为空。");
  if (new TextEncoder().encode(text).length > 65536) throw new Error("文字不能超过 64 KiB。");
  const encrypted = await encryptText(text, session.key);
  const envelope: ClipEnvelope = { id: crypto.randomUUID(), originDeviceId: session.identity.deviceId, type: detectClipType(text), ...encrypted, createdAt: new Date().toISOString(), expiresAt: new Date(Date.now() + 86400_000).toISOString(), version: PROTOCOL_VERSION, keyVersion: session.keyVersion };
  await responseJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: "/api/v1/clips", method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(envelope) }));
  return envelope;
}

export async function fetchPersonalHistory(apiBase: string, session: PersonalSession): Promise<PersonalClip[]> {
  const data = await responseJson(await signedProductionFetch({ apiBase, identity: session.identity, roomId: session.roomId, path: "/api/v1/clips", method: "GET" }));
  return Promise.all((data.clips as ClipEnvelope[]).map(async envelope => {
    try {
      let key = session.key;
      if (envelope.keyVersion !== session.keyVersion) {
        const record = await getVersionedRoomKey(session.roomId, envelope.keyVersion ?? 1);
        if (!record) throw new Error("No historical key");
        key = await importKey(await unwrapVersionedRoomKey(record), { extractable: false });
      }
      return { ...envelope, plaintext: await decryptText(envelope.ciphertext, envelope.nonce, key) };
    } catch { return { ...envelope, plaintext: "此设备没有这条历史记录的密钥。", unavailable: true }; }
  }));
}
